import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Settings2 } from 'lucide-react';
import {
  ApiError,
  api,
  canManageUsers,
  type AuthUser,
  type KickBreakdownItem,
  type KickCandidate,
  type KickWeights,
  type Rank,
} from '@/lib/api';

const RANK_ORDER: Rank[] = ['R5', 'R4', 'R3', 'R2', 'R1'];

export default function AdminTransferWindowKick({ user }: { user: AuthUser }) {
  const { id } = useParams<{ id: string }>();
  const windowId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const windowQuery = useQuery({
    queryKey: ['transfer-window', windowId],
    queryFn: () => api.getTransferWindow(windowId),
    enabled: Number.isInteger(windowId) && windowId > 0,
  });
  const candidatesQuery = useQuery({
    queryKey: ['kick-candidates', windowId],
    queryFn: () => api.getKickCandidates(windowId),
    enabled: Number.isInteger(windowId) && windowId > 0,
  });

  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [filterText, setFilterText] = useState('');
  const [minScore, setMinScore] = useState<number>(0);
  const [rankFilter, setRankFilter] = useState<Set<Rank>>(new Set(RANK_ORDER));
  const [hideAdded, setHideAdded] = useState(true);
  const [weightsOpen, setWeightsOpen] = useState(false);

  const mark = useMutation({
    mutationFn: (ids: number[]) => api.markKickCandidates(windowId, ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfer-window', windowId] });
      qc.invalidateQueries({ queryKey: ['kick-candidates', windowId] });
      navigate(`/admin/transfers/${windowId}`);
    },
  });

  if (!canManageUsers(user.role)) {
    return <div className="text-slate-400">Only R5 can manage transfer windows.</div>;
  }

  if (candidatesQuery.isLoading || windowQuery.isLoading) {
    return <div className="text-slate-500">Loading…</div>;
  }
  const data = candidatesQuery.data;
  const windowData = windowQuery.data;
  if (!data || !windowData) return <div className="text-slate-400">Window not found.</div>;

  const players = data.players;
  const shown = players
    .filter((p) => {
      if (hideAdded && p.alreadyLeaver) return false;
      if (!rankFilter.has(p.rank)) return false;
      if (p.score < minScore) return false;
      if (filterText.trim()) {
        const n = filterText.trim().toLowerCase();
        if (!p.name.toLowerCase().includes(n)) return false;
      }
      return true;
    })
    .sort((a, b) => b.score - a.score);

  const toggleRank = (r: Rank) =>
    setRankFilter((prev) => {
      const next = new Set(prev);
      if (next.has(r)) next.delete(r);
      else next.add(r);
      return next;
    });
  const togglePick = (id: number) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const eligiblePicked = Array.from(picked).filter((id) => {
    const p = players.find((x) => x.playerId === id);
    return p && !p.alreadyLeaver;
  });

  return (
    <div className="space-y-5 pb-32">
      <header className="space-y-1">
        <Link
          to={`/admin/transfers/${windowId}`}
          className="text-xs text-slate-500 hover:text-slate-300"
        >
          ← Back to {windowData.window.name}
        </Link>
        <h1 className="text-xl font-semibold">Kick candidates</h1>
        <p className="text-sm text-slate-400">
          {players.length} active players scored. Higher score = stronger case to kick. Selected
          rows are added to this window's leaver list.
        </p>
      </header>

      <WeightsPanel
        weights={data.weights}
        open={weightsOpen}
        onToggle={() => setWeightsOpen((v) => !v)}
      />

      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/40 p-3">
        <input
          className="input max-w-xs"
          placeholder="Search name…"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />
        <label className="flex items-center gap-1 text-xs text-slate-400">
          Min score
          <input
            className="input w-20 py-1"
            type="number"
            value={minScore}
            onChange={(e) => setMinScore(Number(e.target.value) || 0)}
          />
        </label>
        <div className="flex items-center gap-1 text-xs text-slate-400">
          <span>Ranks:</span>
          {RANK_ORDER.map((r) => {
            const on = rankFilter.has(r);
            return (
              <button
                key={r}
                onClick={() => toggleRank(r)}
                className={`rounded-full border px-2 py-0.5 text-xs ${on ? 'border-indigo-500 bg-indigo-950/50 text-indigo-200' : 'border-slate-700 text-slate-500 hover:bg-slate-800'}`}
              >
                {r}
              </button>
            );
          })}
        </div>
        <label className="ml-auto flex cursor-pointer items-center gap-2 text-xs text-slate-400">
          <input
            type="checkbox"
            checked={hideAdded}
            onChange={(e) => setHideAdded(e.target.checked)}
          />
          Hide already added
        </label>
        <span className="text-xs text-slate-500">
          {shown.length} / {players.length}
        </span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-800">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-slate-900 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="w-10 px-3 py-2"></th>
              <th className="px-3 py-2 text-right">Score</th>
              <th className="px-3 py-2">Player</th>
              <th className="px-3 py-2 text-center">Rank</th>
              <th className="px-3 py-2 text-right">THP</th>
              <th className="px-3 py-2">Breakdown</th>
              <th className="px-3 py-2 text-right">State</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {shown.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-slate-500">
                  No candidates match the filters.
                </td>
              </tr>
            )}
            {shown.map((c) => (
              <CandidateRow
                key={c.playerId}
                candidate={c}
                picked={picked.has(c.playerId)}
                onToggle={() => togglePick(c.playerId)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {eligiblePicked.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-800 bg-slate-950/90 px-4 py-3 backdrop-blur">
          <div className="mx-auto flex max-w-6xl items-center justify-between">
            <span className="text-sm text-slate-300">
              {eligiblePicked.length} selected
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPicked(new Set())}
                className="rounded border border-slate-700 px-3 py-1 text-sm hover:bg-slate-800"
              >
                Clear
              </button>
              <button
                onClick={() => mark.mutate(eligiblePicked)}
                disabled={mark.isPending}
                className="btn-primary"
              >
                {mark.isPending
                  ? 'Adding…'
                  : `Add ${eligiblePicked.length} as leaver${eligiblePicked.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}
      <Style />
    </div>
  );
}

function CandidateRow({
  candidate,
  picked,
  onToggle,
}: {
  candidate: KickCandidate;
  picked: boolean;
  onToggle: () => void;
}) {
  const scoreClass =
    candidate.score >= 30
      ? 'text-red-400'
      : candidate.score >= 10
        ? 'text-amber-300'
        : candidate.score <= -10
          ? 'text-emerald-400'
          : 'text-slate-300';

  return (
    <tr className={picked ? 'bg-slate-900/60' : ''}>
      <td className="px-3 py-2">
        <input
          type="checkbox"
          disabled={candidate.alreadyLeaver}
          checked={picked}
          onChange={onToggle}
        />
      </td>
      <td className={`px-3 py-2 text-right text-lg font-semibold tabular-nums ${scoreClass}`}>
        {candidate.score.toFixed(0)}
      </td>
      <td className="px-3 py-2 font-medium">
        <Link to={`/players/${candidate.playerId}`} className="hover:text-white">
          {candidate.name}
        </Link>
      </td>
      <td className="px-3 py-2 text-center text-xs text-slate-400">{candidate.rank}</td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-300">
        {candidate.thp == null ? '—' : formatThp(candidate.thp)}
      </td>
      <td className="px-3 py-2">
        <BreakdownChips items={candidate.breakdown} />
      </td>
      <td className="px-3 py-2 text-right text-xs">
        {candidate.alreadyLeaver ? (
          <span className="rounded-full bg-amber-950/60 px-2 py-0.5 text-amber-300">
            Leaver
          </span>
        ) : (
          <span className="text-slate-600">—</span>
        )}
      </td>
    </tr>
  );
}

function BreakdownChips({ items }: { items: KickBreakdownItem[] }) {
  if (items.length === 0) {
    return <span className="text-xs text-slate-600">no signals</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((it) => {
        const pos = it.points > 0;
        const cls = pos
          ? 'bg-red-950/40 text-red-200 border-red-900'
          : 'bg-emerald-950/40 text-emerald-200 border-emerald-900';
        return (
          <span
            key={it.key + it.label}
            title={it.detail}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${cls}`}
          >
            <span className="tabular-nums font-semibold">
              {pos ? '+' : '−'}
              {Math.abs(it.points).toFixed(0)}
            </span>
            <span>{it.label}</span>
          </span>
        );
      })}
    </div>
  );
}

const WEIGHT_GROUPS: {
  title: string;
  fields: { key: keyof KickWeights; label: string; hint?: string; step?: number }[];
}[] = [
  {
    title: 'Attendance',
    fields: [
      { key: 'noShowWeight', label: 'No-show weight' },
      { key: 'lateWeight', label: 'Late weight' },
      { key: 'attendanceLookbackDays', label: 'Lookback (days)', step: 1 },
    ],
  },
  {
    title: 'VS',
    fields: [
      { key: 'saveOvershootWeight', label: 'Save overshoot weight (per M)' },
      { key: 'saveOvershootCapTenths', label: 'Save cap (tenths of M/day)', step: 1 },
      { key: 'pushUndershootWeight', label: 'Push undershoot weight (per M)' },
      { key: 'pushTargetTenths', label: 'Push target (tenths of M/day)', step: 1 },
      { key: 'vsLookbackWeeks', label: 'Lookback (weeks)', step: 1 },
    ],
  },
  {
    title: 'Strength',
    fields: [
      { key: 'lowStrengthWeight', label: 'Low-strength penalty' },
      { key: 'highStrengthBonus', label: 'High-strength bonus' },
      { key: 'strengthLowPct', label: 'Low threshold (0–1)' },
      { key: 'strengthHighPct', label: 'High threshold (0–1)' },
    ],
  },
  {
    title: 'Rank',
    fields: [
      { key: 'r1Weight', label: 'R1' },
      { key: 'r2Weight', label: 'R2' },
      { key: 'r3Weight', label: 'R3' },
      { key: 'r4Weight', label: 'R4' },
      { key: 'r5Weight', label: 'R5' },
    ],
  },
  {
    title: 'Leaves',
    fields: [
      { key: 'openEndedLeaveWeight', label: 'Open-ended leave' },
      { key: 'longLeaveDaysThreshold', label: 'Long-leave threshold (days)', step: 1 },
      { key: 'longLeaveWeight', label: 'Long-leave weight' },
    ],
  },
  {
    title: 'Flags',
    fields: [
      { key: 'yellowCardWeight', label: 'Yellow card' },
      { key: 'banWeight', label: 'Red flag / ban' },
    ],
  },
];

function WeightsPanel({
  weights,
  open,
  onToggle,
}: {
  weights: KickWeights;
  open: boolean;
  onToggle: () => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Partial<KickWeights>>({});
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => api.updateKickWeights(draft),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kick-candidates'] });
      setDraft({});
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Failed'),
  });

  const merged: KickWeights = useMemo(() => ({ ...weights, ...draft }), [weights, draft]);
  const dirty = Object.keys(draft).length > 0;

  return (
    <section className="rounded-lg border border-slate-800 bg-slate-900/40">
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium uppercase tracking-wider text-slate-400 hover:text-slate-200"
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Settings2 size={14} />
        Scoring weights
        {dirty && (
          <span className="ml-auto text-xs normal-case text-amber-300">
            unsaved changes
          </span>
        )}
      </button>
      {open && (
        <div className="space-y-4 border-t border-slate-800 p-4">
          {WEIGHT_GROUPS.map((g) => (
            <div key={g.title}>
              <div className="mb-2 text-xs uppercase tracking-wider text-slate-500">
                {g.title}
              </div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                {g.fields.map((f) => (
                  <label key={f.key} className="flex flex-col gap-1 text-xs text-slate-400">
                    <span>{f.label}</span>
                    <input
                      className="input py-1"
                      type="number"
                      step={f.step ?? 0.5}
                      value={(merged[f.key] as number) ?? ''}
                      onChange={(e) => {
                        const v =
                          e.target.value === ''
                            ? undefined
                            : Number(e.target.value);
                        setDraft((prev) => ({ ...prev, [f.key]: v }));
                      }}
                    />
                  </label>
                ))}
              </div>
            </div>
          ))}
          {error && <div className="text-sm text-red-400">{error}</div>}
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => setDraft({})}
              disabled={!dirty}
              className="rounded border border-slate-700 px-3 py-1 text-sm hover:bg-slate-800 disabled:opacity-40"
            >
              Discard
            </button>
            <button
              onClick={() => save.mutate()}
              disabled={!dirty || save.isPending}
              className="btn-primary"
            >
              {save.isPending ? 'Saving…' : 'Save weights'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function formatThp(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toString();
}

function Style() {
  return (
    <style>{`
      .input {
        background: rgb(2 6 23 / 1);
        border: 1px solid rgb(51 65 85 / 1);
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 14px;
        color: inherit;
        width: 100%;
      }
      .input:focus { outline: none; border-color: rgb(100 116 139 / 1); }
      .btn-primary {
        background: rgb(79 70 229 / 1);
        color: white;
        padding: 6px 12px;
        font-size: 14px;
        border-radius: 6px;
      }
      .btn-primary:hover { background: rgb(99 102 241 / 1); }
      .btn-primary:disabled { opacity: 0.5; }
    `}</style>
  );
}
